import { useState, useEffect, useRef, useMemo } from "react";
import "./TimeLineBar.css";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import RestoreIcon from "@mui/icons-material/Restore";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { getForecastLimit } from "../../../utils/weatherTimelineLimits";
import CustomDatePicker from "../../atoms/datePicker/CustomDatePicker";
import CustomTimePicker from "../../atoms/timePicker/CustomTimePicker";

const PLAY_INTERVAL_MS = 5000;
const FALLBACK_FORECAST_DAYS = 5; // used when the user has no max limit
const HOUR_MS = 3600000;
const THUMB_SIZE = 22; // must match .timeline-slider thumb size in the CSS

const pad = (n) => n.toString().padStart(2, "0");

const TimelineBar = ({
  currentTimeZone,
  weatherRequestDate,
  weatherRequestStartHour,
  setWeatherRequestDate,
  setWeatherRequestStartHour,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDateEditor, setShowDateEditor] = useState(false);
  const [dragIndex, setDragIndex] = useState(null); // live value while dragging
  const editorRef = useRef(null);
  const { getTimelineLimits } = useAccessRights();

  const timelineLimits = getTimelineLimits("weather");
  const hasTimelineLimits = timelineLimits !== null;
  const intervalMinJ = timelineLimits?.intervalMinJ ?? -5;
  const intervalMaxJ = timelineLimits?.intervalMaxJ ?? null;

  const baseHour = parseInt(weatherRequestStartHour, 10);

  const currentDateTime = useMemo(() => {
    if (!weatherRequestDate) return null;
    return new Date(
      parseInt(weatherRequestDate.year, 10),
      parseInt(weatherRequestDate.month, 10) - 1,
      parseInt(weatherRequestDate.day, 10),
      baseHour,
    );
  }, [weatherRequestDate, baseHour]);

  const { minDateTime, maxDateTime } = useMemo(() => {
    const nowInFrance = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }),
    );
    const todayDateOnly = new Date(
      nowInFrance.getFullYear(),
      nowInFrance.getMonth(),
      nowInFrance.getDate(),
    );
    const min = new Date(todayDateOnly);
    min.setDate(min.getDate() + intervalMinJ);

    const max =
      hasTimelineLimits && intervalMaxJ !== null
        ? getForecastLimit(intervalMaxJ)
        : null;

    return { minDateTime: min, maxDateTime: max };
  }, [intervalMinJ, intervalMaxJ, hasTimelineLimits]);

  // ---------------- Slider range ----------------
  const totalHours = useMemo(() => {
    let end = maxDateTime;
    if (!end) {
      end = new Date(minDateTime);
      end.setDate(end.getDate() - intervalMinJ + FALLBACK_FORECAST_DAYS);
    }
    return Math.max(1, Math.round((end.getTime() - minDateTime.getTime()) / HOUR_MS));
  }, [minDateTime, maxDateTime, intervalMinJ]);

  const currentIndex = useMemo(() => {
    if (!currentDateTime) return 0;
    const idx = Math.round(
      (currentDateTime.getTime() - minDateTime.getTime()) / HOUR_MS,
    );
    return Math.min(Math.max(idx, 0), totalHours);
  }, [currentDateTime, minDateTime, totalHours]);

  const sliderValue = dragIndex ?? currentIndex;

  const dateFromIndex = (idx) => {
    const d = new Date(minDateTime);
    d.setHours(d.getHours() + idx);
    return d;
  };

  // What the pill shows: the live dragged value if dragging, else the real one
  const displayDateTime =
    dragIndex !== null ? dateFromIndex(dragIndex) : currentDateTime;

  // Labels under the bar
  const ticks = useMemo(() => {
    const stepH =
      totalHours <= 72 ? 6 : totalHours <= 24 * 14 ? 24 : 24 * 3;
    const result = [];
    for (let h = 0; h <= totalHours; h++) {
      const d = new Date(minDateTime);
      d.setHours(d.getHours() + h);
      const hour = d.getHours();
      let show = false;
      if (stepH <= 24) {
        show = hour % stepH === 0;
      } else {
        const dayIdx = Math.round(h / 24);
        show = hour === 0 && dayIdx % (stepH / 24) === 0;
      }
      if (!show) continue;
      result.push({
        h,
        pct: h / totalHours,
        label:
          hour === 0
            ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
            : `${pad(hour)}:00`,
      });
    }
    return result;
  }, [minDateTime, totalHours]);

  const isAtMin =
    !!currentDateTime && currentDateTime.getTime() <= minDateTime.getTime();
  const isAtMax =
    !!currentDateTime &&
    !!maxDateTime &&
    currentDateTime.getTime() >= maxDateTime.getTime();

  useEffect(() => {
    if (!showDateEditor) return;
    const handleClickOutside = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        setShowDateEditor(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDateEditor]);

  const applyClampedDateTime = (dateObj) => {
    let clamped = dateObj;
    if (clamped.getTime() < minDateTime.getTime()) clamped = new Date(minDateTime);
    if (maxDateTime && clamped.getTime() > maxDateTime.getTime())
      clamped = new Date(maxDateTime);

    setWeatherRequestDate?.({
      day: clamped.getDate().toString().padStart(2, "0"),
      month: (clamped.getMonth() + 1).toString().padStart(2, "0"),
      year: clamped.getFullYear().toString(),
    });
    setWeatherRequestStartHour?.(clamped.getHours());
  };

  const shiftBy = (deltaHours) => {
    if (!currentDateTime) return;
    const next = new Date(currentDateTime);
    next.setHours(next.getHours() + deltaHours);
    applyClampedDateTime(next);
  };

  const handlePrevHour = () => {
    if (isAtMin) return;
    shiftBy(-1);
  };
  const handleNextHour = () => {
    if (isAtMax) return;
    shiftBy(1);
  };
  const handlePrevDay = () => {
    if (isAtMin) return;
    shiftBy(-24);
  };
  const handleNextDay = () => {
    if (isAtMax) return;
    shiftBy(24);
  };

  const togglePlay = () => {
    if (isAtMax && !isPlaying) return;
    setIsPlaying((prev) => !prev);
  };

  useEffect(() => {
    if (!isPlaying) return;
    if (isAtMax) {
      setIsPlaying(false);
      return;
    }
    const id = setTimeout(() => {
      shiftBy(1);
    }, PLAY_INTERVAL_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, weatherRequestDate, weatherRequestStartHour]);

  const handleResetDateTime = () => {
    const now = new Date();
    const franceDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Paris" }),
    );
    applyClampedDateTime(franceDate);
  };

  // ---------------- Slider handlers ----------------
  const handleSliderChange = (e) => setDragIndex(Number(e.target.value));

  // Commit only on release so we don't request tiles for every hour we pass
  const commitSlider = () => {
    if (dragIndex === null) return;
    applyClampedDateTime(dateFromIndex(dragIndex));
    setDragIndex(null);
  };

  const formatDate = (d) =>
    d
      ? `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${d.getFullYear()}`
      : "--";
  const formatHour = (d) =>
    d ? `${d.getHours().toString().padStart(2, "0")}:00` : "--:--";

  return (
    <div className="timeline-stepper" data-testid="timeline-bar">
      {showDateEditor && (
        <div
          className="timeline-date-editor-popover"
          ref={editorRef}
          data-testid="timeline-date-editor"
        >
          <CustomDatePicker
            weatherRequestDate={weatherRequestDate}
            weatherRequestStartHour={weatherRequestStartHour}
            onChange={(newDateObj) => setWeatherRequestDate?.(newDateObj)}
            onHourChange={(newHour) => setWeatherRequestStartHour?.(newHour)}
            data-testid="timeline-date-picker"
          />
          <CustomTimePicker
            weatherRequestStartHour={weatherRequestStartHour}
            weatherRequestDate={weatherRequestDate}
            onChange={(newHour) => setWeatherRequestStartHour?.(newHour)}
            data-testid="timeline-time-picker"
          />
          <RestoreIcon
            style={{ cursor: "pointer" }}
            onClick={handleResetDateTime}
            data-testid="timeline-reset-datetime-btn"
          />
        </div>
      )}

      {/* ---------- Slider bar ---------- */}
      <div className="timeline-slider-wrap" data-testid="timeline-slider-wrap">
        <input
          type="range"
          className="timeline-slider"
          min={0}
          max={totalHours}
          step={1}
          value={sliderValue}
          style={{ "--progress": totalHours > 0 ? sliderValue / totalHours : 0 }}
          onChange={handleSliderChange}
          onPointerUp={commitSlider}
          onKeyUp={commitSlider}
          onBlur={commitSlider}
          aria-label="Timeline"
          data-testid="timeline-slider"
        />
        <div className="timeline-slider-ticks">
          {ticks.map((t) => (
            <span
              key={t.h}
              className="timeline-slider-tick"
              style={{
                left: `calc(${THUMB_SIZE / 2}px + (100% - ${THUMB_SIZE}px) * ${t.pct})`,
              }}
            >
              {t.label}
            </span>
          ))}
        </div>
      </div>

      {/* ---------- Pill ---------- */}
      <div className="timeline-pill" data-testid="timeline-pill">
        <button
          type="button"
          className={`timeline-step-btn ${isAtMin ? "timeline-step-btn-disabled" : ""}`}
          onClick={handlePrevDay}
          disabled={isAtMin}
          aria-label="Jour précédent"
          data-testid="timeline-prev-day"
        >
          <KeyboardDoubleArrowLeftIcon fontSize="small" />
        </button>

        <button
          type="button"
          className={`timeline-step-btn ${isAtMin ? "timeline-step-btn-disabled" : ""}`}
          onClick={handlePrevHour}
          disabled={isAtMin}
          aria-label="Heure précédente"
          data-testid="timeline-prev-hour"
        >
          <KeyboardArrowLeftIcon fontSize="small" />
        </button>

        <button
          type="button"
          className={`timeline-play-btn ${isAtMax && !isPlaying ? "timeline-step-btn-disabled" : ""}`}
          onClick={togglePlay}
          disabled={isAtMax && !isPlaying}
          aria-label={isPlaying ? "Pause" : "Lecture"}
          data-testid="timeline-play-pause-button"
        >
          {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </button>

        <span
          className="timeline-pill-date"
          onClick={() => setShowDateEditor((prev) => !prev)}
          data-testid="timeline-date-input"
        >
          {formatDate(displayDateTime)}
        </span>

        <span
          className="timeline-pill-hour"
          onClick={() => setShowDateEditor((prev) => !prev)}
          data-testid="timeline-hour-input"
        >
          {formatHour(displayDateTime)}{" "}
          {currentTimeZone === "UTC+1" ? "CET" : "CEST"}
        </span>

        <button
          type="button"
          className={`timeline-step-btn ${isAtMax ? "timeline-step-btn-disabled" : ""}`}
          onClick={handleNextHour}
          disabled={isAtMax}
          aria-label="Heure suivante"
          data-testid="timeline-next-hour"
        >
          <KeyboardArrowRightIcon fontSize="small" />
        </button>

        <button
          type="button"
          className={`timeline-step-btn ${isAtMax ? "timeline-step-btn-disabled" : ""}`}
          onClick={handleNextDay}
          disabled={isAtMax}
          aria-label="Jour suivant"
          data-testid="timeline-next-day"
        >
          <KeyboardDoubleArrowRightIcon fontSize="small" />
        </button>
      </div>
    </div>
  );
};

export default TimelineBar;
// import { useState, useEffect, useRef, useMemo } from "react";
// import "./TimeLineBar.css";
// import PlayArrowIcon from "@mui/icons-material/PlayArrow";
// import PauseIcon from "@mui/icons-material/Pause";
// import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
// import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
// import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
// import RestoreIcon from "@mui/icons-material/Restore";
// import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
// import { getForecastLimit } from "../../../utils/weatherTimelineLimits";
// import CustomDatePicker from "../../atoms/datePicker/CustomDatePicker";
// import CustomTimePicker from "../../atoms/timePicker/CustomTimePicker";

// const PLAY_INTERVAL_MS = 5000;

// const TimelineBar = ({
//   currentTimeZone,
//   weatherRequestDate,
//   weatherRequestStartHour,
//   setWeatherRequestDate,
//   setWeatherRequestStartHour,
// }) => {
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [showDateEditor, setShowDateEditor] = useState(false);
//   const editorRef = useRef(null);
//   const { getTimelineLimits } = useAccessRights();

//   const timelineLimits = getTimelineLimits("weather");
//   const hasTimelineLimits = timelineLimits !== null;
//   const intervalMinJ = timelineLimits?.intervalMinJ ?? -5;
//   const intervalMaxJ = timelineLimits?.intervalMaxJ ?? null;

//   const baseHour = parseInt(weatherRequestStartHour, 10);

//   const currentDateTime = useMemo(() => {
//     if (!weatherRequestDate) return null;
//     return new Date(
//       parseInt(weatherRequestDate.year, 10),
//       parseInt(weatherRequestDate.month, 10) - 1,
//       parseInt(weatherRequestDate.day, 10),
//       baseHour,
//     );
//   }, [weatherRequestDate, baseHour]);

//   const { minDateTime, maxDateTime } = useMemo(() => {
//     const nowInFrance = new Date(
//       new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }),
//     );
//     const todayDateOnly = new Date(
//       nowInFrance.getFullYear(),
//       nowInFrance.getMonth(),
//       nowInFrance.getDate(),
//     );
//     const min = new Date(todayDateOnly);
//     min.setDate(min.getDate() + intervalMinJ);

//     const max =
//       hasTimelineLimits && intervalMaxJ !== null
//         ? getForecastLimit(intervalMaxJ)
//         : null;

//     return { minDateTime: min, maxDateTime: max };
//   }, [intervalMinJ, intervalMaxJ, hasTimelineLimits]);

//   const isAtMin =
//     !!currentDateTime && currentDateTime.getTime() <= minDateTime.getTime();
//   const isAtMax =
//     !!currentDateTime &&
//     !!maxDateTime &&
//     currentDateTime.getTime() >= maxDateTime.getTime();

//   useEffect(() => {
//     if (!showDateEditor) return;
//     const handleClickOutside = (e) => {
//       if (editorRef.current && !editorRef.current.contains(e.target)) {
//         setShowDateEditor(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [showDateEditor]);

//   const applyClampedDateTime = (dateObj) => {
//     let clamped = dateObj;
//     if (clamped.getTime() < minDateTime.getTime()) clamped = new Date(minDateTime);
//     if (maxDateTime && clamped.getTime() > maxDateTime.getTime())
//       clamped = new Date(maxDateTime);

//     setWeatherRequestDate?.({
//       day: clamped.getDate().toString().padStart(2, "0"),
//       month: (clamped.getMonth() + 1).toString().padStart(2, "0"),
//       year: clamped.getFullYear().toString(),
//     });
//     setWeatherRequestStartHour?.(clamped.getHours());
//   };

//   const shiftBy = (deltaHours) => {
//     if (!currentDateTime) return;
//     const next = new Date(currentDateTime);
//     next.setHours(next.getHours() + deltaHours);
//     applyClampedDateTime(next);
//   };

//   const handlePrevHour = () => {
//     if (isAtMin) return;
//     shiftBy(-1);
//   };
//   const handleNextHour = () => {
//     if (isAtMax) return;
//     shiftBy(1);
//   };
//   const handlePrevDay = () => {
//     if (isAtMin) return;
//     shiftBy(-24);
//   };
//   const handleNextDay = () => {
//     if (isAtMax) return;
//     shiftBy(24);
//   };

//   const togglePlay = () => {
//     if (isAtMax && !isPlaying) return;
//     setIsPlaying((prev) => !prev);
//   };

//   useEffect(() => {
//     if (!isPlaying) return;
//     if (isAtMax) {
//       setIsPlaying(false);
//       return;
//     }
//     const id = setTimeout(() => {
//       shiftBy(1);
//     }, PLAY_INTERVAL_MS);
//     return () => clearTimeout(id);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isPlaying, weatherRequestDate, weatherRequestStartHour]);

//   const handleResetDateTime = () => {
//     const now = new Date();
//     const franceDate = new Date(
//       now.toLocaleString("en-US", { timeZone: "Europe/Paris" }),
//     );
//     applyClampedDateTime(franceDate);
//   };

//   const formatDate = (d) =>
//     d
//       ? `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
//           .toString()
//           .padStart(2, "0")}-${d.getFullYear()}`
//       : "--";
//   const formatHour = (d) =>
//     d ? `${d.getHours().toString().padStart(2, "0")}:00` : "--:--";

//   return (
//     <div className="timeline-stepper" data-testid="timeline-bar">
//       {showDateEditor && (
//         <div
//           className="timeline-date-editor-popover"
//           ref={editorRef}
//           data-testid="timeline-date-editor"
//         >
//           <CustomDatePicker
//             weatherRequestDate={weatherRequestDate}
//             weatherRequestStartHour={weatherRequestStartHour}
//             onChange={(newDateObj) => setWeatherRequestDate?.(newDateObj)}
//             onHourChange={(newHour) => setWeatherRequestStartHour?.(newHour)}
//             data-testid="timeline-date-picker"
//           />
//           <CustomTimePicker
//             weatherRequestStartHour={weatherRequestStartHour}
//             weatherRequestDate={weatherRequestDate}
//             onChange={(newHour) => setWeatherRequestStartHour?.(newHour)}
//             data-testid="timeline-time-picker"
//           />
//           <RestoreIcon
//             style={{ cursor: "pointer" }}
//             onClick={handleResetDateTime}
//             data-testid="timeline-reset-datetime-btn"
//           />
//         </div>
//       )}

//       <div className="timeline-pill" data-testid="timeline-pill">
//         <button
//           type="button"
//           className={`timeline-step-btn ${isAtMin ? "timeline-step-btn-disabled" : ""}`}
//           onClick={handlePrevDay}
//           disabled={isAtMin}
//           aria-label="Jour précédent"
//           data-testid="timeline-prev-day"
//         >
//           <KeyboardDoubleArrowLeftIcon fontSize="small" />
//         </button>

//         <button
//           type="button"
//           className={`timeline-step-btn ${isAtMin ? "timeline-step-btn-disabled" : ""}`}
//           onClick={handlePrevHour}
//           disabled={isAtMin}
//           aria-label="Heure précédente"
//           data-testid="timeline-prev-hour"
//         >
//           <KeyboardArrowLeftIcon fontSize="small" />
//         </button>

//         <button
//           type="button"
//           className={`timeline-play-btn ${isAtMax && !isPlaying ? "timeline-step-btn-disabled" : ""}`}
//           onClick={togglePlay}
//           disabled={isAtMax && !isPlaying}
//           aria-label={isPlaying ? "Pause" : "Lecture"}
//           data-testid="timeline-play-pause-button"
//         >
//           {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
//         </button>

//         <span
//           className="timeline-pill-date"
//           onClick={() => setShowDateEditor((prev) => !prev)}
//           data-testid="timeline-date-input"
//         >
//           {formatDate(currentDateTime)}
//         </span>

//         <span
//           className="timeline-pill-hour"
//           onClick={() => setShowDateEditor((prev) => !prev)}
//           data-testid="timeline-hour-input"
//         >
//           {formatHour(currentDateTime)}{" "}
//           {currentTimeZone === "UTC+1" ? "CET" : "CEST"}
//         </span>

//         <button
//           type="button"
//           className={`timeline-step-btn ${isAtMax ? "timeline-step-btn-disabled" : ""}`}
//           onClick={handleNextHour}
//           disabled={isAtMax}
//           aria-label="Heure suivante"
//           data-testid="timeline-next-hour"
//         >
//           <KeyboardArrowRightIcon fontSize="small" />
//         </button>

//         <button
//           type="button"
//           className={`timeline-step-btn ${isAtMax ? "timeline-step-btn-disabled" : ""}`}
//           onClick={handleNextDay}
//           disabled={isAtMax}
//           aria-label="Jour suivant"
//           data-testid="timeline-next-day"
//         >
//           <KeyboardDoubleArrowRightIcon fontSize="small" />
//         </button>
//       </div>
//     </div>
//   );
// };

// export default TimelineBar;